/**
 * Shared NexaStay helpers: pricing, theme styles, formatting.
 * Used by CheckoutPage, HotelLandingPage, ProfilePage.
 */

import { formatHotelDate } from '../utils/dateUtils';

/** Format YYYY-MM-DD (or other date string) for display using hotel date format. */
export function formatDisplayDate(iso) {
  return formatHotelDate(iso);
}

/** Calendar date to YYYY-MM-DD in the user's local timezone (not UTC — avoids wrong day ahead of UTC). */
export function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Compute room price (base + extra adult/child) for a room type. */
export function computeRoomPrice(rt, roomOccupancy, nights) {
  const baseRate = Number(rt.base_rate ?? rt.final_rate ?? 0);
  if (!baseRate) return 0;
  const baseAdults = rt.base_adults != null ? Number(rt.base_adults) : 1;
  const baseChildren = rt.base_children != null ? Number(rt.base_children) : 0;
  const { adults = baseAdults, children = baseChildren } = roomOccupancy;
  const extraAdults = Math.max(0, adults - baseAdults);
  const extraChildren = Math.max(0, children - baseChildren);
  const extraAdultRate = Number(rt.extra_adult_rate ?? 0) || 0;
  const extraChildRate = Number(rt.extra_child_rate ?? 0) || 0;
  let roomRate = baseRate;
  if (adults === 1 && (rt.single_occupancy_rate != null)) {
    roomRate = (Number(rt.single_occupancy_rate) || 0) + (extraAdults * extraAdultRate) + (extraChildren * extraChildRate);
  } else {
    roomRate = baseRate + (extraAdults * extraAdultRate) + (extraChildren * extraChildRate);
  }
  return roomRate * nights;
}

/** Compute room price when using a specific rate plan row (per-room meal plan). */
export function computeRoomPriceFromRateRow(rt, rateRow, roomOccupancy, nights) {
  if (rateRow == null || (rateRow.base_rate == null && rateRow.final_rate == null)) return 0;
  const baseRate = Number(rateRow.base_rate ?? rateRow.final_rate ?? 0);
  const baseAdults = rt.base_adults != null ? Number(rt.base_adults) : 1;
  const baseChildren = rt.base_children != null ? Number(rt.base_children) : 0;
  const { adults = baseAdults, children = baseChildren } = roomOccupancy;
  const extraAdults = Math.max(0, adults - baseAdults);
  const extraChildren = Math.max(0, children - baseChildren);
  const extraAdultRate = Number(rateRow.extra_adult_rate ?? rt.extra_adult_rate ?? 0) || 0;
  const extraChildRate = Number(rateRow.extra_child_rate ?? rt.extra_child_rate ?? 0) || 0;
  let roomRate = baseRate;
  if (adults === 1 && (rateRow.single_occupancy_rate != null)) {
    roomRate = (Number(rateRow.single_occupancy_rate) || 0) + extraAdults * extraAdultRate + extraChildren * extraChildRate;
  } else {
    roomRate = baseRate + extraAdults * extraAdultRate + extraChildren * extraChildRate;
  }
  return roomRate * nights;
}

/** MUI input sx from NexaStay theme (outlined inputs). */
export function buildInputSx(theme) {
  const accent = theme.accent;
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: theme.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.6)',
      fontSize: '1rem',
      minHeight: 56,
      boxShadow: theme.mode === 'dark' ? 'inset 0 2px 4px rgba(0,0,0,0.2)' : 'inset 0 2px 4px rgba(0,0,0,0.06)',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: `${accent}40`,
        borderWidth: 1.5,
        transition: 'border-color 0.2s, box-shadow 0.2s',
      },
      '&:hover fieldset': { borderColor: `${accent}80` },
      '&.Mui-focused fieldset': {
        borderColor: accent,
        borderWidth: 2,
        boxShadow: `0 0 0 1px ${accent}40`,
      },
      '& input': { color: theme.textPrimary, py: 1.75, px: 2 },
      '& textarea': { color: theme.textPrimary, py: 1.75, px: 2 },
    },
    '& .MuiInputLabel-root': {
      color: theme.textSecondary,
      fontSize: '1rem',
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': { color: accent },
  };
}

/** Lightweight buildInputSx for ProfilePage (minimal fieldset styling). */
export function buildInputSxMinimal(theme) {
  const accent = theme.accent;
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      '& fieldset': { borderColor: `${accent}40` },
      '&:hover fieldset': { borderColor: `${accent}80` },
      '&.Mui-focused fieldset': { borderColor: accent },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
  };
}

/** MUI card sx (3D-style) from NexaStay theme. */
export function buildCard3DSx(theme) {
  const accent = theme.accent;
  return {
    borderRadius: 3,
    bgcolor: theme.cardBg ?? theme.paper,
    border: `1px solid ${accent}26`,
    boxShadow: `0 4px 12px rgba(0,0,0,0.3), 0 0 0 1px ${theme.border}, inset 0 1px 0 rgba(255,255,255,0.06)`,
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 24px rgba(0,0,0,0.4), 0 0 0 1px ${accent}33, inset 0 1px 0 rgba(255,255,255,0.08)`,
    },
  };
}
